import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import toast from 'react-hot-toast'
import emailTemplateService from '../services/emailTemplateService'

const EmailTemplateLibrary = ({ selectionMode = false, onSelectTemplate, isCollapsed: externalCollapsed, onToggleCollapse }) => {
  const { user } = useAuth()
  const { tenant } = useTenant()
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'cold_outreach',
    subject: '',
    body: '',
    variables: [],
    description: ''
  })

  const categories = [
    { id: 'all', name: 'All Templates', icon: '📚' },
    { id: 'cold_outreach', name: 'Cold Outreach', icon: '🎯' },
    { id: 'follow_up', name: 'Follow-up', icon: '📧' },
    { id: 'nurture', name: 'Nurture', icon: '🌱' },
    { id: 'sales', name: 'Sales', icon: '💰' },
    { id: 'onboarding', name: 'Onboarding', icon: '👋' },
    { id: 'custom', name: 'Custom', icon: '✨' }
  ]

  const availableVariables = [
    { name: 'firstName', example: 'John' },
    { name: 'lastName', example: 'Doe' },
    { name: 'email', example: 'john@company.com' },
    { name: 'companyName', example: 'Acme Corp' },
    { name: 'industry', example: 'Technology' },
    { name: 'jobTitle', example: 'CEO' },
    { name: 'phone', example: '+1 555-0100' },
    { name: 'website', example: 'https://acme.com' },
    { name: 'customField1', example: 'Custom Value' },
    { name: 'customField2', example: 'Custom Value 2' }
  ]

  // Load templates from Firestore
  useEffect(() => {
    const loadTemplates = async () => {
      if (!tenant?.id) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        
        // Fetch templates from Firestore
        const fetchedTemplates = await emailTemplateService.getTemplates(tenant.id)
        
        // If no templates exist, initialize default templates
        if (fetchedTemplates.length === 0) {
          await emailTemplateService.initializeDefaultTemplates(tenant.id)
          const defaultTemplates = await emailTemplateService.getTemplates(tenant.id)
          setTemplates(defaultTemplates)
          toast.success('Default templates loaded!')
        } else {
          setTemplates(fetchedTemplates)
        }
      } catch (error) {
        console.error('Error loading templates:', error)
        toast.error('Failed to load templates')
        setTemplates([])
      } finally {
        setIsLoading(false)
      }
    }

    loadTemplates()
  }, [tenant])

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!tenant?.id) {
      toast.error('You must be logged in to save templates')
      return
    }

    try {
      // Extract variables from template body and subject
      const extractedVars = new Set()
      const regex = /\{\{(\w+)\}\}/g
      let match
      
      while ((match = regex.exec(templateForm.subject + ' ' + templateForm.body)) !== null) {
        extractedVars.add(match[1])
      }

      const templateData = {
        ...templateForm,
        variables: Array.from(extractedVars)
      }

      let savedTemplate
      if (isEditing) {
        // Update existing template
        savedTemplate = await emailTemplateService.updateTemplate(selectedTemplate.id, templateData)
        const updatedTemplates = templates.map(t => t.id === savedTemplate.id ? savedTemplate : t)
        setTemplates(updatedTemplates)
        toast.success('Template updated!')
      } else {
        // Create new template
        savedTemplate = await emailTemplateService.createTemplate(tenant.id, templateData)
        setTemplates([...templates, savedTemplate])
        toast.success('Template created!')
      }

      closeModal()
    } catch (error) {
      console.error('Error saving template:', error)
      toast.error('Failed to save template')
    }
  }

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return
    
    try {
      await emailTemplateService.deleteTemplate(templateId)
      const updatedTemplates = templates.filter(t => t.id !== templateId)
      setTemplates(updatedTemplates)
      toast.success('Template deleted')
    } catch (error) {
      console.error('Error deleting template:', error)
      toast.error('Failed to delete template')
    }
  }

  const duplicateTemplate = async (template) => {
    if (!tenant?.id) {
      toast.error('You must be logged in to duplicate templates')
      return
    }

    try {
      const duplicateData = {
        name: `${template.name} (Copy)`,
        category: template.category,
        subject: template.subject,
        body: template.body,
        variables: template.variables || [],
        description: template.description || ''
      }
      
      const newTemplate = await emailTemplateService.createTemplate(tenant.id, duplicateData)
      setTemplates([...templates, newTemplate])
      toast.success('Template duplicated!')
    } catch (error) {
      console.error('Error duplicating template:', error)
      toast.error('Failed to duplicate template')
    }
  }

  const handleSelectTemplate = (template) => {
    if (selectionMode && onSelectTemplate) {
      onSelectTemplate(template)
      toast.success(`Template "${template.name}" selected!`)
    } else {
      openModal(template)
    }
  }

  const openModal = (template = null) => {
    if (template) {
      setIsEditing(true)
      setSelectedTemplate(template)
      setTemplateForm({
        name: template.name,
        category: template.category,
        subject: template.subject,
        body: template.body,
        variables: template.variables || [],
        description: template.description || ''
      })
    } else {
      setIsEditing(false)
      setSelectedTemplate(null)
      setTemplateForm({
        name: '',
        category: 'cold_outreach',
        subject: '',
        body: '',
        variables: [],
        description: ''
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setIsEditing(false)
    setSelectedTemplate(null)
  }

  const insertVariable = (varName) => {
    const variable = `{{${varName}}}`
    setTemplateForm({
      ...templateForm,
      body: templateForm.body + variable
    })
  }

  const previewTemplate = (template) => {
    const preview = {
      subject: template.subject,
      body: template.body
    }
    
    // Replace variables with example values
    availableVariables.forEach(v => {
      const regex = new RegExp(`\\{\\{${v.name}\\}\\}`, 'g')
      preview.subject = preview.subject.replace(regex, v.example)
      preview.body = preview.body.replace(regex, v.example)
    })
    
    return preview
  }

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  // Determine collapse state - use external if provided
  const collapsed = externalCollapsed !== undefined ? externalCollapsed : false

  return (
    <div className="space-y-4">
      {/* Selection Mode Banner */}
      {selectionMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700 font-medium">📌 Click on a template to select it</p>
        </div>
      )}

      {/* Collapsible Header (only when used with external collapse control) */}
      {externalCollapsed !== undefined && onToggleCollapse && (
        <div 
          className="flex justify-between items-center cursor-pointer p-3 rounded-lg transition-colors hover:bg-gray-100"
          onClick={onToggleCollapse}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-lg transition-transform ${collapsed ? '' : 'rotate-90'}`}>
              ▶
            </span>
            <div>
              <h3 className="text-xl font-semibold text-genie-teal flex items-center">
                <span className="mr-2">📧</span>
                Email Templates
                <span className="ml-3 px-2.5 py-1 text-sm rounded-full bg-purple-100 text-purple-700">
                  {templates.length} templates
                </span>
              </h3>
              <p className="text-sm mt-1 text-gray-600">
                {collapsed ? 'Click to expand' : 'Click to collapse'} - Reusable email content
              </p>
            </div>
          </div>
          {!collapsed && (
            <button
              onClick={(e) => { e.stopPropagation(); openModal(); }}
              className="px-4 py-2 bg-genie-teal text-white rounded-lg hover:bg-genie-teal/80 transition-colors"
            >
              ✨ New Template
            </button>
          )}
        </div>
      )}

      {/* Content (only show if not collapsed) */}
      {!collapsed && (
        <>
          {/* Categories & Search */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white rounded-lg p-4">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-sm ${
                    activeCategory === cat.id
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-1">{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>

            {/* Create button for standalone mode */}
            {!externalCollapsed && !selectionMode && (
              <button
                onClick={() => openModal()}
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-medium whitespace-nowrap"
              >
                ✨ Create Template
              </button>
            )}
          </div>

          {/* Templates Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading templates...</p>
              </div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📧</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No templates found</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery ? 'Try a different search term' : 'Create your first email template to get started'}
              </p>
              {!selectionMode && (
                <button
                  onClick={() => openModal()}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-medium"
                >
                  ✨ Create Template
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => {
              const category = categories.find(c => c.id === template.category)
              return (
                <div
                  key={template.id}
                  className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all ${
                    selectionMode ? 'cursor-pointer hover:border-2 hover:border-blue-400' : ''
                  }`}
                  onClick={() => selectionMode && handleSelectTemplate(template)}
                >
                  <div className={`p-3 bg-gradient-to-r ${
                    template.category === 'cold_outreach' ? 'from-blue-500 to-blue-600' :
                    template.category === 'follow_up' ? 'from-green-500 to-green-600' :
                    template.category === 'nurture' ? 'from-purple-500 to-purple-600' :
                    template.category === 'sales' ? 'from-yellow-500 to-yellow-600' :
                    template.category === 'onboarding' ? 'from-pink-500 to-pink-600' :
                    'from-gray-500 to-gray-600'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xl">{category?.icon}</span>
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                        {template.usageCount || 0} uses
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="text-md font-bold text-gray-900 mb-2">{template.name}</h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">{template.description || 'No description'}</p>
                    
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Subject:</div>
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded line-clamp-1">
                        {template.subject}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Variables:</div>
                      <div className="flex flex-wrap gap-1">
                        {template.variables?.length > 0 ? template.variables.slice(0, 5).map(v => (
                          <span key={v} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {`{{${v}}}`}
                          </span>
                        )) : <span className="text-xs text-gray-400">No variables</span>}
                        {template.variables?.length > 5 && (
                          <span className="text-xs text-gray-500">+{template.variables.length - 5} more</span>
                        )}
                      </div>
                    </div>

                    {!selectionMode && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openModal(template); }}
                          className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicateTemplate(template); }}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                          title="Duplicate"
                        >
                          📋
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteTemplate(template.id); }}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            </div>
          )}
        </>
      )}

      {/* Template Editor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white z-10">
              <h2 className="text-2xl font-bold">{isEditing ? 'Edit Template' : 'Create New Template'}</h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Cold Outreach - B2B SaaS"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {/* Category & Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    {categories.filter(c => c.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    placeholder="Brief description..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line *</label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="e.g., Quick question about {{companyName}}"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {/* Email Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Body *</label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="Hi {{firstName}},&#10;&#10;Your message here...&#10;&#10;Best regards"
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none font-mono text-sm"
                />
              </div>

              {/* Available Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insert Variable</label>
                <div className="flex flex-wrap gap-2">
                  {availableVariables.map(v => (
                    <button
                      key={v.name}
                      onClick={() => insertVariable(v.name)}
                      className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 text-sm font-mono"
                      title={`Example: ${v.example}`}
                    >
                      {`{{${v.name}}}`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Click to insert variable into email body. Variables will be replaced with actual contact data.</p>
              </div>

              {/* Preview */}
              {(templateForm.subject || templateForm.body) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview (with example data)</label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-gray-600">Subject:</span>
                      <div className="text-sm text-gray-900 mt-1">
                        {previewTemplate({ subject: templateForm.subject, body: templateForm.body }).subject}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600">Body:</span>
                      <div className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                        {previewTemplate({ subject: templateForm.subject, body: templateForm.body }).body}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                {isEditing ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailTemplateLibrary
